import type { S3ObjectResourceType } from '@mbfab/shared'

// 統一アップロード画面(Step1)で選択できるファイル種別。
// 従来は「ミルシートのアップロード画面」「加工証明のアップロード画面」と画面が分かれており、
// 「剪断証明書の画面からミルシートをアップロードできない」といった分かりづらさがあった。
// そのため種別選択を1画面に集約する（v1 apps/frontend/client/src/components/upload/uploadFileType.ts の移植）。
export type UploadFileType = Extract<
  S3ObjectResourceType,
  'millsheets' | 'millsheetAttachments' | 'shearingCertificates' | 'nestingDiagrams'
>

export type UploadFileTypeOption = {
  resourceType: UploadFileType
  label: string
  // Step1/Step2 で表示する、種別ごとの紐付けルールの説明。
  description: string
}

export const uploadFileTypeOptions: UploadFileTypeOption[] = [
  {
    resourceType: 'millsheets',
    label: 'ミルシート',
    description:
      'ファイル名を「証明書番号_工事名称_注文番号」にすると、対象のミルシートへ自動で紐付きます。' +
      '注文番号を省略して「証明書番号_工事名称」にした場合も、該当ミルシートが1件のときは紐付きます' +
      '（同じ証明書番号・工事名称が注文番号ごとに複数行あるときは、注文番号まで付けてください）。' +
      '工事名称も省略して「証明書番号」のみにした場合は、確認ダイアログでOKするとアップロードでき、' +
      '該当ミルシートが1件のときだけ紐付きます（同じ証明書番号が複数の工事にある場合は、確認欄から手動で紐付けてください）。',
  },
  {
    resourceType: 'millsheetAttachments',
    label: '添付資料',
    description:
      'ミルシートに紐づく切断証明書などの書類です。ファイル名の規則と自動紐付けはミルシートと同じです' +
      '（「証明書番号_工事名称_注文番号」にすると、対象のミルシートへ自動で紐付きます）。' +
      'ミルシート一覧の「添付資料」列に表示され、印刷では原本の直後に出力されます。',
  },
  {
    resourceType: 'shearingCertificates',
    label: '剪断証明書',
    description: 'ファイル名を「剪断証明書番号」にすると、対象の剪断証明書へ自動で紐付きます。',
  },
  {
    resourceType: 'nestingDiagrams',
    label: 'ネスティング図',
    description:
      'ファイル名を「個体番号_証明書番号_重量」にすると、3つが一致する剪断証明書の明細へ自動で紐付きます（重量は整数のkg）。' +
      '「個体番号_証明書番号」にした場合は、該当する明細が1件のときだけ紐付きます。' +
      '「個体番号」のみにした場合は、個体番号が一致する明細へ紐付きます。' +
      '紐付かなかったファイルは、確認欄から個体番号を修正して手動で紐付けてください。',
  },
]

// 添付資料はミルシートと紐付け先・ファイル名規則・紐付け操作が同じなので、画面の部品を共用する。
// 共用の判定を1か所に集約し、種別の追加時に分岐の取りこぼしが起きないようにする
export const isMillsheetLinkedUploadType = (
  resourceType: UploadFileType | undefined,
): resourceType is 'millsheets' | 'millsheetAttachments' =>
  resourceType === 'millsheets' || resourceType === 'millsheetAttachments'
